import React from "react";
import "./App.css";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import DashboardLayout from "./layouts/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import Index from "./pages/Index";
import UserManagement from "./pages/UserManagement";
import TestimonialManagement from "./pages/TestimonialManagement";
import Auth from "./pages/Auth";
import { Toaster } from "@/components/ui/sonner";
import { AuthGuard } from "./components/AuthGuard";
import UserProfilePage from "./pages/UserProfilePage";
import TestimonialPreview from "./pages/TestimonialPreview";
import UnsplashTest from "./pages/UnsplashTest";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import BlogManagement from "./pages/BlogManagement";
import BlogPostEditor from "./pages/BlogPostEditor";
import BlogPostPreviewPage from "./pages/BlogPostPreviewPage";
import BlogCalendar from "./pages/BlogCalendar";
import ContentCalendar from "./pages/ContentCalendar";
import { ContentLoader } from "./components/ContentLoader";
import { ErrorBoundary } from "./components/ErrorBoundary";
import MediaLibrary from "./pages/MediaLibrary";
import NewsletterManagement from "./pages/NewsletterManagement";
import { CampaignBuilder } from "@/components/newsletter/campaigns/CampaignBuilder";
import { UnsubscribePage } from "@/components/newsletter/UnsubscribePage";
import EmailAnalyticsDashboard from "./pages/EmailAnalyticsDashboard";
import LinkedInManagement from "./pages/LinkedInManagement";
import LinkedInPostEditor from "./pages/LinkedInPostEditor";

// Define the routes
const router = createBrowserRouter([
  {
    path: "/",
    element: <Index />,
  },
  {
    path: "/auth",
    element: <Auth />,
  },
  {
    path: "/dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Dashboard />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  
  // Add the new unified ContentCalendar route
  {
    path: "/dashboard/calendar",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <ContentCalendar />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  
  {
    path: "/dashboard/users",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <UserManagement />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/users/:id",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <UserProfilePage />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/testimonials",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <TestimonialManagement />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/testimonials/:id",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <TestimonialPreview />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/unsplash",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <UnsplashTest />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  
  // LinkedIn routes
  {
    path: "/dashboard/linkedin",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <LinkedInManagement />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/linkedin/posts/new",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <LinkedInPostEditor />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/linkedin/posts/:id",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <LinkedInPostEditor />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  
  // Keep existing blog routes
  {
    path: "/dashboard/blog",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogManagement />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/blog/posts",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogManagement />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/blog/posts/new",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogPostEditor />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/blog/posts/:id",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogPostEditor />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/blog/preview/:slug",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogPostPreviewPage />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/blog/preview/id/:id",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogPostPreviewPage />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/blog/new",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogPostEditor />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/blog/edit/:id",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogPostEditor />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/blog/preview/:id",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogPostPreviewPage />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  // We'll keep the old blog calendar route for compatibility during migration
  {
    path: "/dashboard/blog/calendar",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <BlogCalendar />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/media",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <MediaLibrary />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/blog",
    element: <Blog />,
  },
  {
    path: "/blog/:slug",
    element: <BlogPost />,
  },
  // Newsletter management routes
  {
    path: "/dashboard/newsletter",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <NewsletterManagement />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/newsletter/campaigns/new",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <CampaignBuilder />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/newsletter/campaigns/edit/:id",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <CampaignBuilder />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "/dashboard/newsletter/analytics",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <EmailAnalyticsDashboard />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  
  // Public routes
  {
    path: "/unsubscribe",
    element: <UnsubscribePage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

// The main App component no longer uses any hooks that depend on Router context
function App() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
      <Toaster position="top-right" />
    </ErrorBoundary>
  );
}

export default App;
